import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { LexicalEditor } from 'lexical';
import { useEffect, useRef } from 'react';
import type { RichTextValue } from './rich-text-input.tsx';

export const internalRichTextInputStateUpdateTag = 'rich-text-update-internal';

export function editorAlreadyHasValue(editor: LexicalEditor, value: RichTextValue): boolean {
    const current = editor.getEditorState().toJSON();
    // it's important we parse the incoming value because lexical fills in some
    // default values and also orders the keys alphabetically.
    const incoming = editor.parseEditorState(value).toJSON();

    return JSON.stringify(current) === JSON.stringify(incoming);
}

// Syncs external `value` prop changes into the Lexical editor. LexicalComposer only
// reads `initialConfig.editorState` on mount, so without this the input is effectively
// uncontrolled after the first render. The stringify check skips the sync when the
// editor already holds the incoming value — which happens right after our own
// OnChangePlugin emits a change up to the parent, avoiding a set/emit loop.
export function ValueSyncPlugin({ value }: { value: RichTextValue | null | undefined }): null {
    const [editor] = useLexicalComposerContext();
    const initialValueRef = useRef(value);

    useEffect(() => {
        if (value === undefined || value === null) {
            return;
        }

        // Skip syncing the initial value: the composer already applied it on mount,
        // and re-applying it will dirty the parent form.
        if (value === initialValueRef.current) {
            return;
        }

        if (editorAlreadyHasValue(editor, value)) {
            return;
        }

        editor.setEditorState(editor.parseEditorState(value), {
            tag: internalRichTextInputStateUpdateTag,
        });
    }, [editor, value]);

    return null;
}

// The counterpart for `editable`: a field that locks part way through an async
// action needs the change pushed in, since `initialConfig.editable` is long read.
export function EditableSyncPlugin({ editable }: { editable: boolean }): null {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        editor.setEditable(editable);
    }, [editor, editable]);

    return null;
}

type AutoFocusPluginProps = {
    defaultSelection?: 'rootStart' | 'rootEnd';
};

// A fork of Lexical's own `AutoFocusPlugin`, which shares this name — import it
// from here, not from `@lexical/react`. Ours corrects the case below, where the
// Selection API moves but the Focus API does not follow.
export function AutoFocusPlugin({ defaultSelection = 'rootEnd' }: AutoFocusPluginProps): null {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        editor.focus(
            () => {
                // If we try and move selection to the same point with setBaseAndExtent, it won't
                // trigger a re-focus on the element. So in the case this occurs, we'll need to correct it.
                // Normally this is fine, Selection API !== Focus API, but for the editor
                // implementation the bound focus and selection APIs together.
                const activeElement = document.activeElement;
                const rootElement = editor.getRootElement();
                if (
                    rootElement !== null &&
                    (activeElement === null || !rootElement.contains(activeElement))
                ) {
                    rootElement.focus({ preventScroll: true });
                }
            },
            { defaultSelection },
        );
    }, [defaultSelection, editor]);

    return null;
}
